<template>
  <div style="padding: 8px">
    <div style="display: flex; justify-content: flex-end; margin-bottom: 8px">
      <a-button
        type="primary"
        size="small"
        style="width: 70px; margin-right: 8px"
        @click="handleSearch"
      >
        查询
      </a-button>
      <a-button
        size="small"
        style="width: 70px"
        @click="handleReset"
      >
        重置
      </a-button>
    </div>
    <a-input-group
      compact
      size="small"
      style="display: flex"
    >
      <a-select
        v-if="condition.operator_list?.length > 0 && searchType === 'advanced'"
        :dropdownMatchSelectWidth="false"
        :value="condition.operator"
        @change="onChange('operator', $event)"
        size="small"
      >
        <a-select-option
          v-for="item in condition.operator_list"
          :value="item"
          :key="item"
        >
          {{ operator_type_map[item] }}
        </a-select-option>
      </a-select>
      <template v-if="['is_or_not_blank_or_null', 'is_or_not_null'].includes(condition.operator)">
        <SmallTableSerarchField
          :is_show_label="false"
          :label="column.dataIndex"
          :value="fieldValue"
          :field="{
            ...condition,
            type: 'select',
            mode: 'default',
            format: 'choices',
            class_name: 'str',
            placeholder: '',
          }"
          :formModel="formModel"
          :options="is_or_not_blank_or_null_options"
          :validateInfos="validateInfos"
          @update:value="onChange('value', $event)"
          :field_width="condition.field_width || 155"
        />
      </template>
      <template v-else-if="['between', 'notbetween'].includes(condition.operator)">
        <template v-if="condition.type === 'datepicker'">
          <SmallTableSerarchField
            :is_show_label="false"
            :label="column.dataIndex"
            :value="fieldValue"
            :field="{
              ...condition,
              type: 'rangepicker',
              format: condition.dateFormat,
            }"
            :formModel="formModel"
            :validateInfos="validateInfos"
            :field_width="condition.field_width ? condition.field_width * 2 : 320"
            @update:value="onChange('value', $event)"
          />
        </template>
        <template v-else-if="condition.type === 'monthpicker'">
          <SmallTableSerarchField
            :is_show_label="false"
            :label="column.dataIndex"
            :value="fieldValue"
            :field="{
              ...condition,
              type: 'monthrangepicker',
            }"
            :formModel="formModel"
            :validateInfos="validateInfos"
            :field_width="condition.field_width ? condition.field_width * 2 : 320"
            @update:value="onChange('value', $event)"
          />
        </template>
        <template v-else>
          <SmallTableSerarchField
            :is_show_label="false"
            :label="column.dataIndex"
            :value="fieldValue?.[0]"
            :field="condition"
            :formModel="formModel"
            :options="condition.options"
            :validateInfos="validateInfos"
            :field_width="condition.field_width || 155"
            @update:value="onChange('value', $event, 0)"
          />
          <span
            class="range"
            style="padding: 0 4px; line-height: 24px"
          >
            ~
          </span>
          <SmallTableSerarchField
            :is_show_label="false"
            :label="column.dataIndex"
            :value="fieldValue?.[1]"
            :field="condition"
            :formModel="formModel"
            :options="condition.options"
            :validateInfos="validateInfos"
            :field_width="condition.field_width || 155"
            @update:value="onChange('value', $event, 1)"
          />
        </template>
      </template>
      <template v-else-if="condition.operator === '_eq'">
        <SmallTableSerarchField
          :is_show_label="false"
          :label="column.dataIndex"
          :value="fieldValue"
          :field="{
            ...condition,
            type: 'select',
            mode: 'default',
            format: 'choices',
            class_name: 'str',
            placeholder: '',
          }"
          :formModel="formModel"
          :options="options_for_date_eq"
          :validateInfos="validateInfos"
          :field_width="condition.field_width || 155"
          @update:value="onChange('value', $event)"
        />
      </template>
      <template v-else-if="condition.type === 'datepicker' || condition.type === 'monthpicker'">
        <SmallTableSerarchField
          :is_show_label="false"
          :label="column.dataIndex"
          :value="fieldValue"
          :field="{ ...condition, format: condition.dateFormat }"
          :formModel="formModel"
          :options="condition.options"
          :validateInfos="validateInfos"
          :field_width="condition.field_width || 155"
          @update:value="onChange('value', $event)"
        />
      </template>
      <template v-else>
        <SmallTableSerarchField
          :is_show_label="false"
          :label="column.dataIndex"
          :value="fieldValue"
          :field="{
            ...condition,
            name: column.dataIndex,
          }"
          :formModel="formModel"
          :options="condition.options"
          :validateInfos="validateInfos"
          :field_width="condition.field_width || 155"
          @update:value="onChange('value', $event)"
        />
      </template>
    </a-input-group>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import SmallTableSerarchField from 'liyu-pc-base/components/FormRender/SmallTableSerarchField.vue';
import {
  getDefaultSearchOperator,
  getParamsCount,
  operator_type_map,
  options_for_date_eq,
} from './utils';
import { Column, SearchConditionItem, SearchConfig, SearchType } from './typing';
import { isArray } from 'lodash';
import { ConditionOperator } from 'liyu-pc-base/api/typing';

const props = defineProps({
  column: {
    type: Object as PropType<Column>,
    required: true,
  },
  searchConfig: {
    type: Array as PropType<SearchConfig[]>,
    required: true,
  },
  searchConditions: {
    type: Array as PropType<SearchConditionItem[]>,
    required: true,
  },
  searchType: {
    type: String as PropType<SearchType>,
    default: 'advanced',
  },
  confirm: {
    type: Function as PropType<() => void>,
    required: true,
  },
  clearFilters: {
    type: Function as PropType<() => void>,
    required: true,
  },
});

const emit = defineEmits(['search', 'reset', 'update:searchConditions']);

const { t } = useI18n();
const formModel = ref<Record<string, any>>({});
const validateInfos = ref({});

const is_or_not_blank_or_null_options = ref([
  { value: 'yes', label: t('is_or_not_blank_or_null.yes') },
  { value: 'no', label: t('is_or_not_blank_or_null.no') },
]);

const condition = computed<any>(() => {
  const dataIndex = props.column.dataIndex;
  const currentCondition = props.searchConditions.find(item => item.field_name === dataIndex);
  const configItem = props.searchConfig?.find(item => item.field_name === dataIndex);
  if (currentCondition) {
    return {
      ...configItem,
      ...currentCondition,
    };
  }
  return {
    ...configItem,
    operator: getDefaultSearchOperator(configItem),
    value: undefined,
  };
});

const fieldValue = computed(() => {
  const dataIndex = props.column.dataIndex;
  const configItem = props.searchConfig?.find(item => item.field_name === dataIndex);
  if (!configItem) return undefined;
  const value_count = getParamsCount(condition.value.operator, configItem);
  let value = condition.value.value;
  if (value_count == 1) {
    if (value instanceof Array) {
      return value[0] || undefined;
    } else {
      return value || undefined;
    }
  } else if (value_count == 2) {
    if (value instanceof Array) {
      return [value[0], value[1]];
    } else if (value) {
      return [value, null];
    } else {
      return [null, null];
    }
  } else {
    return value;
  }
});

const onChange = (type: 'value' | 'operator', newValue: any, array_index?: number) => {
  const field_name = props.column.dataIndex!;
  const conditions = [...props.searchConditions];
  const configItem = props.searchConfig.find(item => item.field_name === field_name);
  if (!configItem) return;
  let value: any;
  let operator: ConditionOperator;
  let currentCondition = conditions.find(item => item.field_name === field_name);

  if (type === 'value') {
    operator = currentCondition
      ? currentCondition.operator
      : (getDefaultSearchOperator(configItem) as ConditionOperator);
    if (array_index !== undefined) {
      const value_count = getParamsCount(operator, configItem);
      value = currentCondition?.value;
      if (!value || !isArray(value)) {
        value = new Array(value_count).fill(null);
      } else {
        value = [...value];
      }
      value[array_index] = newValue;
    } else {
      value = isArray(newValue)
        ? newValue
        : newValue !== undefined && newValue !== null
        ? [newValue]
        : [];
    }
  } else {
    operator = newValue as ConditionOperator;
    const value_count = getParamsCount(newValue, configItem);
    value = value_count ? new Array(value_count).fill(null) : undefined;
  }

  if (currentCondition) {
    currentCondition.operator = operator;
    currentCondition.value = value;
  } else {
    conditions.push({
      field_name,
      value,
      operator: operator,
      format: configItem?.format || 'input',
      class_name: configItem?.class_name || 'str',
    });
  }

  formModel.value[field_name] = isArray(value) ? value[0] : value;
  emit('update:searchConditions', conditions);
};

const handleSearch = () => {
  const dataIndex = props.column.dataIndex!;
  const activeCondition = props.searchConditions.find(item => item.field_name === dataIndex);
  let searchVal = '';
  if (activeCondition) {
    searchVal = isArray(activeCondition.value) ? activeCondition.value[0] : activeCondition.value;
  }
  emit('search', { confirm: props.confirm, dataIndex, value: searchVal });
};

const handleReset = () => {
  emit('reset', { clearFilters: props.clearFilters, dataIndex: props.column.dataIndex });
};
</script>
